import * as React from "react";
import { createPortal } from "react-dom";
import { Search, Tag, MapPin, ChevronDown, Check, Package } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { useSpaces } from "@/hooks/useSpaces.js";

/**
 * InventoryPicker — the shared item-selection surface (F-c).
 *
 * A multi-select list over the household's inventory with the SAME filter set
 * as the main inventory list: free-text search, location (space/container)
 * filter, and tag filter. Extracted so the goal item-linking modal
 * (`GoalItemsPanel`) and the placement-trial "items to move" list reuse one
 * component instead of a bespoke, thinner list — keeping the two in sync.
 *
 * Data source is `GET /api/items`, which already supports `search`, `spaceId`
 * (comma-separated), `tags` (comma-separated), and `sort`/`limit`/`offset`.
 * Selection is controlled by the parent via `selectedIds` + `onChange`.
 */

export interface PickerItem {
  id: string;
  name: string;
  quantity: number;
  spaceId: string | null;
  spaceName: string | null;
  tags: string[];
  photoUrl: string | null;
}

const PAGE_SIZE = 50;

export function InventoryPicker({
  selectedIds,
  onChange,
  excludeItemIds,
  items: itemsProp,
  onItemsLoaded,
  className,
  listMaxHeight = "max-h-64",
}: {
  /** Currently-selected item IDs (controlled). */
  selectedIds: string[];
  /** Called with the next selected-ID set when the user toggles an item. */
  onChange: (ids: string[]) => void;
  /** Item IDs to hide from the list entirely (e.g. already-linked items). */
  excludeItemIds?: string[];
  /** When provided, the picker renders THIS fixed set of items (client-supplied)
   *  and does NOT fetch from the API — used by the trial "items to move" list,
   *  whose candidate set is the goal's already-linked items (a small, complete,
   *  in-memory set that must not be truncated by server pagination). Search and
   *  the location/tag filters still apply, computed locally over these items. */
  items?: PickerItem[];
  /** Notifies the parent of the full item objects currently loaded, so callers
   *  that need space/name metadata for selected items can resolve it. */
  onItemsLoaded?: (items: PickerItem[]) => void;
  className?: string;
  /** Tailwind max-height class for the scrollable list region. */
  listMaxHeight?: string;
}) {
  const { spaces } = useSpaces();
  const clientMode = itemsProp !== undefined;
  const [fetched, setFetched] = React.useState<PickerItem[]>([]);
  const [search, setSearch] = React.useState("");
  const [filterTag, setFilterTag] = React.useState<string[]>([]);
  const [filterSpace, setFilterSpace] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(!clientMode);
  const [loadError, setLoadError] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchItems = React.useCallback(async (pageNum = 1, append = false) => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (filterTag.length) p.set("tags", filterTag.join(","));
    if (filterSpace.length) p.set("spaceId", filterSpace.join(","));
    p.set("limit", String(PAGE_SIZE));
    p.set("offset", String((pageNum - 1) * PAGE_SIZE));
    setLoadError(false);
    if (!append) setLoading(true);
    try {
      const data = await apiFetch<PickerItem[]>(`/api/items?${p}`);
      setFetched((prev) => (append ? [...prev, ...data] : data));
      setHasMore(data.length === PAGE_SIZE);
      setPage(pageNum);
    } catch {
      if (!append) setLoadError(true);
    }
    setLoading(false);
  }, [search, filterTag, filterSpace]);

  // Server-fetch mode only: debounce search; refetch on any filter change.
  // In client mode (items supplied), filtering is applied locally below.
  React.useEffect(() => {
    if (clientMode) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { fetchItems(1); }, 250);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [clientMode, fetchItems]);

  // The base set: either the client-supplied items or the fetched page(s).
  const baseItems = clientMode ? (itemsProp ?? []) : fetched;

  // Surface the base items to the parent (for name/space resolution).
  React.useEffect(() => { onItemsLoaded?.(baseItems); }, [baseItems, onItemsLoaded]);

  const excluded = React.useMemo(() => new Set(excludeItemIds ?? []), [excludeItemIds]);
  const visibleItems = React.useMemo(() => {
    let list = baseItems.filter((i) => !excluded.has(i.id));
    if (clientMode) {
      // Apply search + filters locally over the supplied set.
      const q = search.trim().toLowerCase();
      if (q) list = list.filter((i) => i.name.toLowerCase().includes(q));
      if (filterSpace.length) list = list.filter((i) => i.spaceId && filterSpace.includes(String(i.spaceId)));
      if (filterTag.length) list = list.filter((i) => (i.tags ?? []).some((t) => filterTag.includes(t)));
    }
    return list;
  }, [baseItems, excluded, clientMode, search, filterSpace, filterTag]);

  // All tags present in the base items (drives the tag popover).
  const allTags = React.useMemo(
    () => [...new Set(baseItems.flatMap((i) => i.tags ?? []))],
    [baseItems]
  );

  // Disambiguate duplicate space names by parent (mirrors InventoryPage).
  const spaceDisplayName = React.useMemo(() => {
    const roomSpaces = spaces.filter((s) => s.type === "room" || s.type === "container");
    const nameCount: Record<string, number> = {};
    roomSpaces.forEach((s) => { nameCount[s.name] = (nameCount[s.name] || 0) + 1; });
    const parentMap: Record<string, string> = {};
    spaces.forEach((s) => { parentMap[String(s.id)] = s.name; });
    const displayMap: Record<string, string> = {};
    roomSpaces.forEach((s) => {
      const sid = String(s.id);
      displayMap[sid] =
        nameCount[s.name] > 1 && s.parentId && parentMap[String(s.parentId)]
          ? `${s.name} (${parentMap[String(s.parentId)]})`
          : s.name;
    });
    return displayMap;
  }, [spaces]);

  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search items..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-border/40 text-sm focus:outline-none focus:border-primary"
        />
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center flex-wrap">
        <LocationFilter
          spaces={spaces}
          spaceDisplayName={spaceDisplayName}
          filterSpace={filterSpace}
          setFilterSpace={setFilterSpace}
        />
        <TagFilterPopover allTags={allTags} filterTag={filterTag} setFilterTag={setFilterTag} />
        {(filterTag.length > 0 || filterSpace.length > 0) && (
          <button
            onClick={() => { setFilterTag([]); setFilterSpace([]); }}
            className="px-3 py-1.5 rounded-full text-xs font-medium text-red-500 bg-red-50"
          >
            Clear
          </button>
        )}
      </div>

      {/* List */}
      <div className={cn("overflow-y-auto space-y-1", listMaxHeight)}>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => <div key={i} className="h-11 bg-gray-100 rounded-lg animate-pulse" />)}
          </div>
        ) : loadError ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-400 mb-2">Couldn't load items</p>
            <button onClick={() => fetchItems(1)} className="text-xs text-primary font-medium hover:text-primary/80">Try again</button>
          </div>
        ) : visibleItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            {baseItems.length > 0 ? "All matching items are already added" : "No items found"}
          </p>
        ) : (
          <>
            {visibleItems.map((item) => {
              const active = selectedIds.includes(item.id);
              return (
                <label
                  key={item.id}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggle(item.id)}
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary shrink-0"
                  />
                  {item.photoUrl ? (
                    <img src={item.photoUrl} alt="" loading="lazy" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <Package className="w-4 h-4 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700 block truncate">{item.name}</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {item.spaceName && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                          <MapPin className="w-2.5 h-2.5" />{item.spaceName}
                        </span>
                      )}
                      {(item.tags ?? []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t}</span>
                      ))}
                    </div>
                  </div>
                </label>
              );
            })}
            {!clientMode && hasMore && (
              <button
                onClick={() => fetchItems(page + 1, true)}
                className="w-full py-2 rounded-lg bg-gray-50 text-xs text-gray-500 hover:bg-gray-100"
              >
                Load more
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* Location filter — portal dropdown, matches InventoryPage behavior. */
function LocationFilter({
  spaces,
  spaceDisplayName,
  filterSpace,
  setFilterSpace,
}: {
  spaces: { id: string; name: string; type: string; parentId: string | null }[];
  spaceDisplayName: Record<string, string>;
  filterSpace: string[];
  setFilterSpace: (ids: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const [style, setStyle] = React.useState<React.CSSProperties>({});
  const roomSpaces = spaces.filter((s) => s.type === "room" || s.type === "container");

  React.useEffect(() => {
    if (!open) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setStyle({ position: "fixed", top: rect.bottom + 4, left: rect.left, zIndex: 9999 });
    }
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target)) return;
      const dropdown = document.getElementById("inventory-picker-location-dropdown");
      if (dropdown?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className={cn(
          "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1",
          filterSpace.length > 0 ? "bg-primary text-white" : "bg-muted text-gray-600"
        )}
      >
        <MapPin className="w-3 h-3" />
        {filterSpace.length > 0
          ? `${spaceDisplayName[filterSpace[0]] ?? "Location"}${filterSpace.length > 1 ? ` +${filterSpace.length - 1}` : ""}`
          : "Location"}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && createPortal(
        <div
          id="inventory-picker-location-dropdown"
          style={style}
          className="bg-white rounded-xl border border-neutral-border/30 shadow-lg p-2 min-w-[200px] max-h-60 overflow-y-auto"
        >
          {roomSpaces.length === 0 ? (
            <p className="text-xs text-gray-400 px-2 py-1">No spaces created yet</p>
          ) : (
            <>
              {filterSpace.length > 0 && (
                <button
                  onClick={() => { setFilterSpace([]); setOpen(false); }}
                  className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 mb-1"
                >
                  Clear selection
                </button>
              )}
              {roomSpaces.map((space) => {
                const sid = String(space.id);
                const active = filterSpace.includes(sid);
                return (
                  <button
                    key={space.id}
                    onClick={() => setFilterSpace(active ? filterSpace.filter((x) => x !== sid) : [...filterSpace, sid])}
                    className={cn(
                      "w-full text-left px-3 py-1.5 rounded-lg text-xs flex items-center gap-2",
                      active ? "bg-primary/10 text-primary font-medium" : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <MapPin className="w-3 h-3" />
                    {spaceDisplayName[sid] ?? space.name}
                    {active && <Check className="w-3 h-3 ml-auto" />}
                  </button>
                );
              })}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/* Searchable tag filter popover — matches InventoryPage behavior. */
function TagFilterPopover({
  allTags,
  filterTag,
  setFilterTag,
}: {
  allTags: string[];
  filterTag: string[];
  setFilterTag: (tags: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [tagSearch, setTagSearch] = React.useState("");
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = tagSearch.trim()
    ? allTags.filter((t) => t.toLowerCase().includes(tagSearch.toLowerCase()))
    : allTags;

  if (allTags.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); setTagSearch(""); }}
        className={cn(
          "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1",
          filterTag.length > 0 ? "bg-primary text-white" : "bg-muted text-gray-600"
        )}
      >
        <Tag className="w-3 h-3" />
        {filterTag.length > 0 ? `${filterTag[0]}${filterTag.length > 1 ? ` +${filterTag.length - 1}` : ""}` : "Tags"}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-52 bg-white rounded-xl border border-neutral-border/30 shadow-lg z-50 p-2">
          <input
            autoFocus
            placeholder="Search tags..."
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            className="w-full px-3 py-1.5 rounded-lg border border-neutral-border/30 text-xs mb-2 focus:outline-none focus:border-primary"
          />
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {filterTag.length > 0 && (
              <button onClick={() => { setFilterTag([]); setOpen(false); }} className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50">Clear tags</button>
            )}
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 px-3 py-1">No matching tags</p>
            ) : (
              filtered.map((tag) => {
                const active = filterTag.includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => setFilterTag(active ? filterTag.filter((t) => t !== tag) : [...filterTag, tag])}
                    className={cn(
                      "w-full text-left px-3 py-1.5 rounded-lg text-xs flex items-center gap-2",
                      active ? "bg-primary/10 text-primary font-medium" : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                    {active && <Check className="w-3 h-3 ml-auto" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
